import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/common/Logo';

const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    dateOfBirth: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUserData } = useAuth();

  const yearsSince = (dob: string) => {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const ms = Date.now() - d.getTime();
    return ms / (365.25 * 24 * 60 * 60 * 1000);
  };
  const isMinor = (() => {
    const age = yearsSince(formData.dateOfBirth);
    return age !== null && age < 16;
  })();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.role) {
      toast.error('Please select your role');
      return;
    }

    if (!formData.email || !formData.firstName || !formData.lastName || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (isMinor) {
      toast.error('Players under 16 must be signed up by a parent. Redirecting...');
      navigate('/signup/parent');
      return;
    }

    setLoading(true);

    try {
      // Create account directly without OTP verification
      const response = await api.post('/auth/signup', {
        ...formData,
        sports: ['chess'] // Default sport for now, can be changed later
      });
      // Backend now returns MINOR_REQUIRES_GUARDIAN if server-side age check fails.

      if (response.data.success) {
        toast.success(response.data.message || 'Account created successfully!');
        
        // Store token and user data in context and localStorage
        const { token, user } = response.data.data;
        setUserData(user, token);
        
        // Navigate based on role - both players and coaches go through sports selection
        navigate('/complete-signup', {
          state: { user: user }
        });
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Check for specific error codes (handle both ApiError format and raw Axios format)
      const errorCode = error.code || error.response?.data?.error?.code;
      const errorMessage = error.message || error.response?.data?.error?.message;
      
      if (errorCode === 'USER_EXISTS') {
        toast.error('This email already exists');
      } else if (errorCode === 'MINOR_REQUIRES_GUARDIAN') {
        toast.error('Under 16 — redirecting to parent signup');
        navigate('/signup/parent');
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error('Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <Link to="/" className="text-gray-600 hover:text-gray-800 mb-4 inline-block text-sm">
            ← Back
          </Link>
          <Link to="/" aria-label="Sportofolo home" className="inline-block mb-4">
            <Logo variant="app-icon" className="w-16 h-16 mx-auto" />
          </Link>
          <div className="flex justify-center space-x-2 mb-5">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Create Account</h1>
          <p className="text-gray-600 mt-2">Join the Sportofolo community</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400">👤</span>
                </div>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className="input-field pl-10"
                  required
                />
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400">👤</span>
                </div>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className="input-field pl-10"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">📧</span>
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                className="input-field pl-10"
                required
              />
            </div>
          </div>

          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">📱</span>
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number (optional)"
                className="input-field pl-10"
              />
            </div>
          </div>

          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">🔒</span>
              </div>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                className="input-field pl-10"
                required
                minLength={6}
              />
            </div>
          </div>

          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">🎂</span>
              </div>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                max={new Date().toISOString().slice(0, 10)}
                className="input-field pl-10"
                required
              />
            </div>
            {isMinor && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Players under 16 need a parent account.{' '}
                <Link to="/signup/parent" className="underline font-semibold">
                  Sign up as parent instead
                </Link>
              </p>
            )}
          </div>

          <div>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="input-field"
              required
            >
              <option value="">Choose your role</option>
              <option value="player">Player</option>
              <option value="coach">Coach</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Signing up a child under 16?{' '}
            <Link to="/signup/parent" className="text-blue-600 hover:text-blue-800 font-medium">
              Sign up as parent
            </Link>
          </p>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-yellow-600 hover:text-yellow-800 font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;